import React, { useState, useEffect, useCallback } from 'react';
import './CashInvoiceManagement.css';
import { API_BASE_URL, authFetch } from '../../config/api.config';

/**
 * Barrechnungen und Bordverkäufe (Anlage v1.4 Abschnitt 2.7 Punkt 10):
 * Barrechnungen mit eigenem Nummernkreis (BAR-JJJJ-NNNN) ohne Kundenbezug sowie
 * Zusatzverkäufe an Bord mit Zuordnung zu einer Buchung und Rechnungsstellung.
 */

interface CashInvoiceItem {
  id: number;
  description: string;
  quantity: number;
  grossPrice: number;
  taxRate: number;
  lineTotal: number;
}

interface CashInvoice {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  bookingId?: number | null;
  bookingNumber?: string | null;
  customerName?: string | null;
  notes?: string | null;
  totalGross: number;
  status: string;
  createdBy: string;
  createdAt: string;
  items: CashInvoiceItem[];
}

interface BookingSearchResult {
  id: number;
  bookingNumber: string;
  customerName: string;
  bookingDate: string;
  // T-085 (v1.8.19): Fahrttermin und Abfahrt aus dem Schedule (bookingDate ist das Anlagedatum)
  scheduleDate?: string | null;
  startTime?: string | null;
  status: string;
}

interface ItemFormRow {
  description: string;
  quantity: string;
  grossPrice: string;
  taxRate: string; // '0.19' | '0.07' | '0'
}

const emptyItemRow = (): ItemFormRow => ({
  description: '',
  quantity: '1',
  grossPrice: '',
  taxRate: '0.19'
});

const CashInvoiceManagement: React.FC = () => {
  const [invoices, setInvoices] = useState<CashInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog "Neue Barrechnung / Bordverkauf"
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [bookingResults, setBookingResults] = useState<BookingSearchResult[]>([]);
  const [bookingSearching, setBookingSearching] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingSearchResult | null>(null);
  const [notes, setNotes] = useState('');
  const [itemRows, setItemRows] = useState<ItemFormRow[]>([emptyItemRow()]);
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  // Deutsche Eingaben mit Komma zulassen
  const parsePrice = (value: string): number => {
    const parsed = parseFloat(value.replace(',', '.'));
    return isNaN(parsed) ? NaN : parsed;
  };

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());

      // T-009: authFetch setzt die Auth-Header und refresht bei 401 automatisch
      const response = await authFetch(`${API_BASE_URL}/cash-invoices?${params.toString()}`, {});
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setInvoices(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err: any) {
      setError(`Fehler beim Laden der Barrechnungen: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, searchTerm]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const searchBookings = async () => {
    try {
      setBookingSearching(true);
      const params = new URLSearchParams();
      if (bookingSearchTerm.trim()) params.append('search', bookingSearchTerm.trim());

      const response = await authFetch(`${API_BASE_URL}/cash-invoices/bookings?${params.toString()}`, {});
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setBookingResults(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(`Fehler bei der Buchungssuche: ${err.message}`);
    } finally {
      setBookingSearching(false);
    }
  };

  const downloadPdf = async (invoice: CashInvoice) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/cash-invoices/${invoice.id}/pdf`, {});
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(`Fehler beim PDF-Download: ${err.message}`);
    }
  };

  const cancelInvoice = async (invoice: CashInvoice) => {
    const confirmed = window.confirm(
      `Barrechnung ${invoice.invoiceNumber} wirklich stornieren?\n\nDie Rechnung bleibt erhalten, der Nummernkreis bleibt lückenlos.`
    );
    if (!confirmed) return;

    try {
      setCancellingId(invoice.id);
      const response = await authFetch(`${API_BASE_URL}/cash-invoices/${invoice.id}/cancel`, {
        method: 'POST'
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
      }
      setSuccessMessage(`Barrechnung ${invoice.invoiceNumber} wurde storniert.`);
      await loadInvoices();
    } catch (err: any) {
      setError(`Fehler beim Stornieren: ${err.message}`);
    } finally {
      setCancellingId(null);
    }
  };

  const openDialog = () => {
    setSelectedBooking(null);
    setBookingSearchTerm('');
    setBookingResults([]);
    setNotes('');
    setItemRows([emptyItemRow()]);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const updateItemRow = (index: number, field: keyof ItemFormRow, value: string) => {
    setItemRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addItemRow = () => setItemRows((prev) => [...prev, emptyItemRow()]);

  const removeItemRow = (index: number) => {
    setItemRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  // Laufende Summenanzeige
  const runningTotal = itemRows.reduce((sum, row) => {
    const quantity = parseInt(row.quantity, 10);
    const price = parsePrice(row.grossPrice);
    if (isNaN(quantity) || isNaN(price) || quantity < 1 || price < 0) return sum;
    return sum + quantity * price;
  }, 0);

  const validateForm = (): string | null => {
    if (itemRows.length === 0) return 'Es muss mindestens eine Position erfasst werden.';
    for (let i = 0; i < itemRows.length; i++) {
      const row = itemRows[i];
      if (!row.description.trim()) return `Position ${i + 1}: Bitte eine Beschreibung eingeben.`;
      const quantity = parseInt(row.quantity, 10);
      if (isNaN(quantity) || quantity < 1) return `Position ${i + 1}: Die Menge muss mindestens 1 betragen.`;
      const price = parsePrice(row.grossPrice);
      if (isNaN(price) || price < 0) return `Position ${i + 1}: Bitte einen gültigen Bruttopreis eingeben.`;
    }
    if (notes.length > 500) return 'Die Notiz darf höchstens 500 Zeichen lang sein.';
    return null;
  };

  const saveInvoice = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const body = {
        bookingId: selectedBooking ? selectedBooking.id : null,
        notes: notes.trim() || null,
        items: itemRows.map((row) => ({
          description: row.description.trim(),
          quantity: parseInt(row.quantity, 10),
          grossPrice: parsePrice(row.grossPrice),
          taxRate: parseFloat(row.taxRate)
        }))
      };

      const response = await authFetch(`${API_BASE_URL}/cash-invoices`, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
      }

      const created: CashInvoice = await response.json();
      setSuccessMessage(
        selectedBooking
          ? `Bordverkauf ${created.invoiceNumber} über ${formatCurrency(created.totalGross)} erfasst.`
          : `Barrechnung ${created.invoiceNumber} über ${formatCurrency(created.totalGross)} erstellt.`
      );
      setDialogOpen(false);
      await loadInvoices();
    } catch (err: any) {
      setError(`Fehler beim Speichern: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cashinv-management">
      <div className="cashinv-header">
        <h1>Barrechnungen und Bordverkäufe</h1>
        <button className="cashinv-btn-primary" onClick={openDialog}>
          Neue Barrechnung / Bordverkauf
        </button>
      </div>

      {error && (
        <div className="cashinv-error">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {successMessage && (
        <div className="cashinv-success">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)}>✕</button>
        </div>
      )}

      <div className="cashinv-filter-bar">
        <label>
          Von
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label>
          Bis
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <input
          type="text"
          className="cashinv-search"
          placeholder="Suche nach Rechnungsnr., Notiz oder Buchungsnr. ..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="cashinv-count">{invoices.length} Rechnungen</div>
      </div>

      {loading && invoices.length === 0 ? (
        <div className="cashinv-loading">Lädt Barrechnungen ...</div>
      ) : (
        <table className="cashinv-table">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Datum</th>
              <th>Typ</th>
              <th className="cashinv-col-amount">Betrag</th>
              <th>Status</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id} className={invoice.status === 'Storniert' ? 'cashinv-row-cancelled' : ''}>
                <td className="cashinv-number">{invoice.invoiceNumber}</td>
                <td>{formatDate(invoice.invoiceDate)}</td>
                <td>
                  {invoice.bookingId ? (
                    <span title={invoice.customerName || ''}>
                      Bordverkauf ({invoice.bookingNumber || `Buchung ${invoice.bookingId}`})
                    </span>
                  ) : (
                    'Barrechnung'
                  )}
                </td>
                <td className="cashinv-col-amount">{formatCurrency(invoice.totalGross)}</td>
                <td>
                  <span
                    className={`cashinv-status ${invoice.status === 'Storniert' ? 'cashinv-status-cancelled' : 'cashinv-status-created'}`}
                  >
                    {invoice.status}
                  </span>
                </td>
                <td className="cashinv-actions">
                  <button className="cashinv-btn-action" onClick={() => downloadPdf(invoice)} title="PDF herunterladen">
                    PDF
                  </button>
                  {invoice.status !== 'Storniert' && (
                    <button
                      className="cashinv-btn-action cashinv-btn-cancel"
                      onClick={() => cancelInvoice(invoice)}
                      disabled={cancellingId !== null}
                      title="Barrechnung stornieren"
                    >
                      {cancellingId === invoice.id ? 'Storniert ...' : 'Stornieren'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && invoices.length === 0 && (
        <div className="cashinv-empty">
          <p>Keine Barrechnungen im gewählten Zeitraum vorhanden.</p>
          <button className="cashinv-btn-primary" onClick={openDialog}>
            Erste Barrechnung erstellen
          </button>
        </div>
      )}

      {dialogOpen && (
        <div className="cashinv-dialog-overlay" onClick={closeDialog}>
          <div className="cashinv-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="cashinv-dialog-header">
              <h2>Neue Barrechnung / Bordverkauf</h2>
              <button className="cashinv-dialog-close" onClick={closeDialog} disabled={saving}>
                ✕
              </button>
            </div>

            <div className="cashinv-dialog-body">
              {/* Optionale Buchungszuordnung (Bordverkauf) */}
              <div className="cashinv-section">
                <h3>Buchung zuordnen (optional, für Bordverkauf)</h3>
                {selectedBooking ? (
                  <div className="cashinv-selected-booking">
                    <div>
                      <strong>{selectedBooking.bookingNumber}</strong>, {selectedBooking.customerName},{' '}
                      Fahrt {formatDate(selectedBooking.scheduleDate || selectedBooking.bookingDate)}{selectedBooking.startTime ? `, ${selectedBooking.startTime} Uhr` : ''}
                    </div>
                    <button className="cashinv-btn-action" onClick={() => setSelectedBooking(null)} disabled={saving}>
                      Zuordnung entfernen
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="cashinv-booking-search">
                      <input
                        type="text"
                        placeholder="Buchungsnummer oder Kundenname suchen ..."
                        value={bookingSearchTerm}
                        onChange={(e) => setBookingSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') searchBookings();
                        }}
                      />
                      <button className="cashinv-btn-secondary" onClick={searchBookings} disabled={bookingSearching}>
                        {bookingSearching ? 'Sucht ...' : 'Suchen'}
                      </button>
                    </div>
                    {bookingResults.length > 0 && (
                      <ul className="cashinv-booking-results">
                        {bookingResults.map((booking) => (
                          <li key={booking.id}>
                            <button
                              onClick={() => {
                                setSelectedBooking(booking);
                                setBookingResults([]);
                              }}
                            >
                              <strong>{booking.bookingNumber}</strong>, {booking.customerName},{' '}
                              Fahrt {formatDate(booking.scheduleDate || booking.bookingDate)}{booking.startTime ? `, ${booking.startTime} Uhr` : ''}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
                <p className="cashinv-hint">
                  Ohne Buchungszuordnung wird eine reine Barrechnung ohne Kundenbezug erstellt.
                </p>
              </div>

              {/* Positionen */}
              <div className="cashinv-section">
                <h3>Positionen</h3>
                <table className="cashinv-items-table">
                  <thead>
                    <tr>
                      <th>Beschreibung</th>
                      <th className="cashinv-col-qty">Menge</th>
                      <th className="cashinv-col-price">Bruttopreis</th>
                      <th className="cashinv-col-tax">MwSt.</th>
                      <th className="cashinv-col-sum">Summe</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemRows.map((row, index) => {
                      const quantity = parseInt(row.quantity, 10);
                      const price = parsePrice(row.grossPrice);
                      const lineTotal =
                        !isNaN(quantity) && !isNaN(price) && quantity >= 1 && price >= 0 ? quantity * price : 0;
                      return (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              value={row.description}
                              maxLength={500}
                              placeholder="z.B. Bier 0,5l"
                              onChange={(e) => updateItemRow(index, 'description', e.target.value)}
                            />
                          </td>
                          <td className="cashinv-col-qty">
                            <input
                              type="number"
                              min={1}
                              step={1}
                              value={row.quantity}
                              onChange={(e) => updateItemRow(index, 'quantity', e.target.value)}
                            />
                          </td>
                          <td className="cashinv-col-price">
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="0,00"
                              value={row.grossPrice}
                              onChange={(e) => updateItemRow(index, 'grossPrice', e.target.value)}
                            />
                          </td>
                          <td className="cashinv-col-tax">
                            <select value={row.taxRate} onChange={(e) => updateItemRow(index, 'taxRate', e.target.value)}>
                              <option value="0.19">19 %</option>
                              <option value="0.07">7 %</option>
                              <option value="0">0 %</option>
                            </select>
                          </td>
                          <td className="cashinv-col-sum">{formatCurrency(lineTotal)}</td>
                          <td>
                            <button
                              className="cashinv-btn-remove"
                              onClick={() => removeItemRow(index)}
                              disabled={itemRows.length <= 1 || saving}
                              title="Position entfernen"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <button className="cashinv-btn-secondary" onClick={addItemRow} disabled={saving}>
                  + Position hinzufügen
                </button>
              </div>

              {/* Notiz */}
              <div className="cashinv-section">
                <h3>Notiz (optional)</h3>
                <textarea
                  value={notes}
                  maxLength={500}
                  rows={2}
                  placeholder="z.B. Verkauf an Bord, Floß 2"
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {/* Laufende Summe */}
              <div className="cashinv-total-bar">
                <span>Gesamtsumme brutto:</span>
                <strong>{formatCurrency(runningTotal)}</strong>
              </div>
            </div>

            <div className="cashinv-dialog-footer">
              <button className="cashinv-btn-secondary" onClick={closeDialog} disabled={saving}>
                Abbrechen
              </button>
              <button className="cashinv-btn-primary" onClick={saveInvoice} disabled={saving}>
                {saving ? 'Speichert ...' : selectedBooking ? 'Bordverkauf speichern' : 'Barrechnung speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashInvoiceManagement;
